/**
 * Engineering materials catalog — synced from GET /app-config; updates via PUT /app-config/engineering-materials-catalog.
 * Each entry has a stable slug (identity for project quantities), an editable name, and a footage/quantity unit.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { getApiBase } from "../apiBase";
import { buildPluginsHeader } from "./registry";
import { canManageEngineeringMaterialsCatalog } from "./userMatrixStorage";

export const ENGINEERING_MATERIALS_STORAGE_KEY = "pb.plugin.engineeringMaterialsCatalog.v1";

/** @type {{ materials: { slug: string, name: string, unit: 'footage'|'quantity' }[] } | null} */
let catalogCache = null;

export const DEFAULT_ENGINEERING_MATERIALS = [
  { name: 'Conduit Feet 1.25"', unit: "footage" },
  { name: 'Conduit Feet 1.5"', unit: "footage" },
  { name: 'Conduit Feet 1.5" w/ Tracer Wire', unit: "footage" },
  { name: 'Clear Lock Couplers 1.25"', unit: "quantity" },
  { name: 'Clear Lock Couplers 1.5"', unit: "quantity" },
  { name: 'Tri Poly Duct Plug 1.5"', unit: "quantity" },
  { name: 'Simplex Plug 1.5"', unit: "quantity" },
  { name: "Duct Plug w/Loop Nut", unit: "quantity" },
  { name: "NEMA Box", unit: "quantity" },
  { name: "Handholes/Vaults 24x36x24", unit: "quantity" },
  { name: "Handholes/Vaults 36x60x36 Polymer Concrete", unit: "quantity" },
  { name: "Handholes/Vaults 36x60x48 Bulk8 HS20 w/Iron Frame and Cover", unit: "quantity" },
  { name: "Handholes/Vaults 48x48x48 Manhole", unit: "quantity" },
  { name: "Marker Balls", unit: "quantity" },
  { name: "Test Station", unit: "quantity" },
  { name: "2 Point Isolation Switch Test Station", unit: "quantity" },
  { name: "5 Point Isolation Switch Test Station", unit: "quantity" },
  { name: "Tracer Wire", unit: "quantity" },
  { name: "Tracer Wire Connectors", unit: "quantity" },
  { name: "Ground Rods", unit: "quantity" },
  { name: "Ground Rod Connectors", unit: "quantity" },
  { name: "12/10mm Microduct", unit: "footage" },
  { name: "12/10mm Plenum Microduct", unit: "footage" },
  { name: "12/10mm Microduct Coupler", unit: "quantity" },
  { name: "Marker Warning Sign", unit: "quantity" },
  { name: "Fiber 12 Count", unit: "footage" },
  { name: "Fiber 72 Count", unit: "footage" },
  { name: "Fiber 144 Count", unit: "footage" },
  { name: "Fiber 576 Count", unit: "footage" },
  { name: "Fiber 864 Count", unit: "footage" },
  { name: "Fiber 1728 Count", unit: "footage" },
];

export function slugifyMaterialName(raw) {
  const s = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/["'`´]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return s.slice(0, 80);
}

export function uniqueMaterialSlug(desired, used) {
  const base = slugifyMaterialName(desired) || "material";
  if (!used.has(base)) return base;
  let n = 2;
  while (used.has(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
}

function normalizeUnit(raw) {
  const s = String(raw || "")
    .trim()
    .toLowerCase();
  if (s === "footage" || s === "ft" || s === "feet") return "footage";
  return "quantity";
}

function defaultMaterialsWithSlugs() {
  const used = new Set();
  return DEFAULT_ENGINEERING_MATERIALS.map((m) => {
    const slug = uniqueMaterialSlug(m.name, used);
    used.add(slug);
    return { slug, name: m.name, unit: normalizeUnit(m.unit) };
  });
}

export function normalizeEngineeringMaterialsCatalog(raw) {
  return normalizeCatalog(raw);
}

function normalizeCatalog(raw) {
  const list = Array.isArray(raw?.materials) ? raw.materials : Array.isArray(raw) ? raw : null;
  if (!list?.length) {
    return { materials: defaultMaterialsWithSlugs() };
  }
  const usedSlugs = new Set();
  const materials = [];
  for (const entry of list) {
    if (!entry || typeof entry !== "object") continue;
    const name = String(entry.name ?? entry.item ?? "").trim();
    const slugHint = String(entry.slug ?? "").trim();
    if (!name && !slugHint) continue;
    const slug = uniqueMaterialSlug(slugHint || name, usedSlugs);
    usedSlugs.add(slug);
    materials.push({ slug, name: name || slug, unit: normalizeUnit(entry.unit) });
  }
  if (!materials.length) {
    return { materials: defaultMaterialsWithSlugs() };
  }
  return { materials };
}

export function hydrateEngineeringMaterialsCatalogFromServer(raw) {
  const normalized = normalizeCatalog(raw);
  catalogCache = normalized;
  try {
    localStorage.setItem(ENGINEERING_MATERIALS_STORAGE_KEY, JSON.stringify(normalized));
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent("pb-engineering-materials-catalog-changed"));
}

export function clearEngineeringMaterialsCatalogHydration() {
  catalogCache = null;
}

export function getEngineeringMaterialsCatalog() {
  if (catalogCache) return catalogCache;
  try {
    const raw = localStorage.getItem(ENGINEERING_MATERIALS_STORAGE_KEY);
    if (raw) {
      const parsed = normalizeCatalog(JSON.parse(raw));
      catalogCache = parsed;
      return parsed;
    }
  } catch {
    /* ignore */
  }
  catalogCache = normalizeCatalog(null);
  return catalogCache;
}

/**
 * Legacy ACF used "Fiber (1728-ct)" which slugifies to fiber_1728_ct, while the
 * catalog uses "Fiber 1728 Count" / fiber_1728_count. Return extra lookup keys.
 */
export function materialAliasCandidates(raw) {
  const v = String(raw || "").trim();
  if (!v) return [];
  const lower = v.toLowerCase();
  const slug = slugifyMaterialName(v);
  const paren = lower.match(/^fiber\s*\(\s*(\d+)\s*-?\s*ct\s*\)$/);
  const ctSlug = slug.match(/^fiber_(\d+)_ct$/);
  const bareSlug = !paren && !ctSlug ? slug.match(/^fiber_(\d+)$/) : null;
  const n = paren?.[1] || ctSlug?.[1] || bareSlug?.[1];
  if (!n) return [];
  return [`fiber_${n}_count`, `Fiber ${n} Count`];
}

export function findCatalogMaterial(catalog, slugOrName) {
  const v = String(slugOrName || "").trim();
  if (!v) return null;
  const materials = catalog?.materials || getEngineeringMaterialsCatalog().materials;
  const tryKeys = [v, slugifyMaterialName(v), ...materialAliasCandidates(v)].filter(Boolean);
  const seen = new Set();
  for (const key of tryKeys) {
    const lower = String(key).toLowerCase();
    if (seen.has(lower)) continue;
    seen.add(lower);
    const bySlug = materials.find((m) => String(m.slug).toLowerCase() === lower);
    if (bySlug) return bySlug;
    const byName = materials.find((m) => String(m.name).trim().toLowerCase() === lower);
    if (byName) return byName;
  }
  return null;
}

export function displayNameForMaterial(slugOrName, catalog = getEngineeringMaterialsCatalog()) {
  const hit = findCatalogMaterial(catalog, slugOrName);
  if (hit?.name) return hit.name;
  return String(slugOrName || "").trim();
}

export function unitLabelForMaterialName(itemName, catalog = getEngineeringMaterialsCatalog()) {
  const hit = findCatalogMaterial(catalog, itemName);
  return hit?.unit === "footage" ? "Footage" : "Quantity";
}

function authHeaders() {
  const h = { ...buildPluginsHeader() };
  try {
    const token = localStorage.getItem("projectBoardAuthToken");
    if (token) h.Authorization = `Bearer ${token}`;
  } catch {
    /* ignore */
  }
  return h;
}

export async function saveEngineeringMaterialsCatalog(catalog) {
  const normalized = normalizeCatalog(catalog);
  const { data } = await axios.put(
    `${getApiBase()}/app-config/engineering-materials-catalog`,
    normalized,
    { headers: authHeaders() }
  );
  if (data?.engineeringMaterialsCatalog) {
    hydrateEngineeringMaterialsCatalogFromServer(data.engineeringMaterialsCatalog);
  } else {
    hydrateEngineeringMaterialsCatalogFromServer(normalized);
  }
  return getEngineeringMaterialsCatalog();
}

/** Re-read catalog when the shared list is saved elsewhere in the app. */
export function useEngineeringMaterialsCatalog() {
  const [rev, setRev] = useState(0);
  useEffect(() => {
    const onChange = () => setRev((r) => r + 1);
    window.addEventListener("pb-engineering-materials-catalog-changed", onChange);
    return () => window.removeEventListener("pb-engineering-materials-catalog-changed", onChange);
  }, []);
  return useMemo(() => {
    void rev;
    return getEngineeringMaterialsCatalog();
  }, [rev]);
}

export function MaterialCatalogSelect({
  value,
  onChange,
  catalog,
  className = "eng-materials-select",
}) {
  const namesAndSlugs = useMemo(() => {
    const list = catalog?.materials || [];
    const v = String(value || "").trim();
    if (!v) return list;
    const hit = findCatalogMaterial(catalog, v);
    if (hit) return list;
    return [...list, { slug: slugifyMaterialName(v) || v, name: v, unit: "quantity" }];
  }, [catalog, value]);

  const selectValue = useMemo(() => {
    const v = String(value || "").trim();
    if (!v) return "";
    const hit = findCatalogMaterial(catalog, v);
    return hit?.slug || v;
  }, [catalog, value]);

  return (
    <select
      className={className}
      value={selectValue}
      disabled={onChange == null}
      onChange={
        onChange
          ? (e) => {
              const slug = e.target.value;
              const hit = (catalog?.materials || []).find((m) => m.slug === slug);
              onChange(slug, hit || null);
            }
          : undefined
      }
    >
      <option value="">Select material…</option>
      {namesAndSlugs.map((m) => (
        <option key={m.slug || m.name} value={m.slug || m.name}>
          {m.name}
        </option>
      ))}
    </select>
  );
}

export function EngineeringMaterialsCatalogEditor({ viewerEmail }) {
  const canManage = canManageEngineeringMaterialsCatalog(viewerEmail);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState([]);
  const [newName, setNewName] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [newUnit, setNewUnit] = useState("quantity");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [rev, setRev] = useState(0);
  const draftRef = useRef([]);
  const openRef = useRef(false);

  const reloadDraft = useCallback(() => {
    const next = getEngineeringMaterialsCatalog().materials.map((m) => ({ ...m }));
    draftRef.current = next;
    setDraft(next);
  }, []);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    if (!canManage) return undefined;
    reloadDraft();
    const onChange = () => {
      // Don't wipe in-progress edits while the panel is open.
      if (openRef.current) return;
      reloadDraft();
      setRev((r) => r + 1);
    };
    window.addEventListener("pb-engineering-materials-catalog-changed", onChange);
    return () => window.removeEventListener("pb-engineering-materials-catalog-changed", onChange);
  }, [canManage, reloadDraft]);

  if (!canManage) return null;

  const patchDraftRow = (index, patch) => {
    setDraft((prev) => {
      const next = prev.map((row, i) => (i === index ? { ...row, ...patch } : row));
      draftRef.current = next;
      return next;
    });
    setError("");
  };

  const onSave = async () => {
    setSaving(true);
    setError("");
    try {
      const used = new Set();
      const materials = (draftRef.current || []).map((m) => {
        const name = String(m.name || "").trim();
        const slug = uniqueMaterialSlug(String(m.slug || "").trim() || name, used);
        used.add(slug);
        return {
          slug,
          name: name || slug,
          unit: m.unit === "footage" ? "footage" : "quantity",
        };
      });
      const saved = await saveEngineeringMaterialsCatalog({ materials });
      draftRef.current = saved.materials.map((m) => ({ ...m }));
      setDraft(draftRef.current);
      setOpen(false);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Could not save materials list.");
    } finally {
      setSaving(false);
    }
  };

  const addMaterial = () => {
    const name = String(newName || "").trim();
    if (!name) return;
    if (draft.some((m) => String(m.name).trim().toLowerCase() === name.toLowerCase())) {
      setError("That material is already on the list.");
      return;
    }
    const used = new Set(draft.map((m) => String(m.slug || "").toLowerCase()));
    const slug = uniqueMaterialSlug(String(newSlug || "").trim() || name, used);
    if (draft.some((m) => String(m.slug).toLowerCase() === slug.toLowerCase())) {
      setError("That slug is already on the list.");
      return;
    }
    setDraft((prev) => {
      const next = [...prev, { slug, name, unit: newUnit === "footage" ? "footage" : "quantity" }];
      draftRef.current = next;
      return next;
    });
    setNewName("");
    setNewSlug("");
    setNewUnit("quantity");
    setError("");
  };

  const removeMaterial = (index) => {
    setDraft((prev) => {
      const next = prev.filter((_, i) => i !== index);
      draftRef.current = next;
      return next;
    });
  };

  return (
    <div className="eng-materials-catalog" data-catalog-rev={rev}>
      <button
        type="button"
        className="project-field-file-btn eng-materials-catalog-toggle"
        onClick={() => {
          if (!open) reloadDraft();
          setOpen((v) => !v);
        }}
      >
        {open ? "Hide materials list" : "Manage materials list"}
      </button>
      {open ? (
        <div className="eng-materials-catalog-panel">
          <p className="project-detail-muted">
            Names and units can change freely. The slug is the stable id — project quantities stay
            attached as long as the slug does not change. Click Save list after adding or editing.
          </p>
          <ul className="eng-materials-catalog-list">
            {draft.map((m, index) => (
              <li key={m.slug || `${m.name}-${index}`}>
                <input
                  className="eng-materials-catalog-name"
                  type="text"
                  aria-label="Material name"
                  value={m.name}
                  onChange={(e) => patchDraftRow(index, { name: e.target.value })}
                />
                <input
                  className="eng-materials-catalog-slug"
                  type="text"
                  aria-label="Material slug"
                  value={m.slug}
                  onChange={(e) => {
                    const next = slugifyMaterialName(e.target.value);
                    patchDraftRow(index, { slug: next || m.slug });
                  }}
                />
                <select
                  className="eng-materials-catalog-unit"
                  aria-label="Amount type"
                  value={m.unit === "footage" ? "footage" : "quantity"}
                  onChange={(e) =>
                    patchDraftRow(index, { unit: e.target.value === "footage" ? "footage" : "quantity" })
                  }
                >
                  <option value="quantity">Quantity</option>
                  <option value="footage">Footage</option>
                </select>
                <button
                  type="button"
                  className="project-field-file-btn danger"
                  onClick={() => removeMaterial(index)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
          <div className="eng-materials-catalog-add">
            <input
              type="text"
              placeholder="New material name"
              value={newName}
              onChange={(e) => {
                const name = e.target.value;
                setNewName(name);
                if (!String(newSlug || "").trim()) {
                  /* slug field stays empty so Add derives it from the name */
                }
              }}
            />
            <input
              type="text"
              placeholder="slug (optional)"
              value={newSlug}
              onChange={(e) => setNewSlug(slugifyMaterialName(e.target.value))}
            />
            <select value={newUnit} onChange={(e) => setNewUnit(e.target.value)}>
              <option value="quantity">Quantity</option>
              <option value="footage">Footage</option>
            </select>
            <button type="button" className="project-field-file-btn" onClick={addMaterial}>
              Add
            </button>
          </div>
          {error ? (
            <p className="project-detail-error" role="alert">
              {error}
            </p>
          ) : null}
          <div className="eng-materials-catalog-actions">
            <button type="button" className="project-dashboard-edit-toggle" disabled={saving} onClick={onSave}>
              {saving ? "Saving…" : "Save list"}
            </button>
            <button
              type="button"
              className="project-field-file-btn"
              disabled={saving}
              onClick={() => {
                reloadDraft();
                setOpen(false);
                setError("");
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
